
















































import {Component, Prop, Vue, Watch} from 'vue-property-decorator'
import LoadingSpinner from '@/components/LoadingSpinner.vue'

const maxRecords = 100 * 1000

export enum DataType {
  Text = 'Text',
  Number = 'Number',
  Email = 'Email',
  Date = 'Date',
}

export interface Field {
  name: string
  title: string
  callback?: string
  component?: Vue
  dataType?: DataType
  hidden?: boolean
  sort?: boolean
  sorted?: boolean
  sortDirection?: number
}

@Component({components: {LoadingSpinner}})
export default class DataGrid extends Vue {
  @Prop(Array) public fields?: Field[]
  @Prop(Array) public data?: object[]
  @Prop() public dataRetriever?: (offset?: number, count?: number) => Promise<object[]>
  @Prop() public refresh?: number
  @Prop() public showCount?: boolean
  private tableFields: any = []
  private loading = false
  private fetchLimit = 500
  private fetchOffset = 0
  private fetchLock: number[] = []
  private scrollElement: HTMLElement | null = null
  public tableData: any[] = []
  public cursor = "auto"

  public mounted() {
    this.scrollElement = document.getElementById('modalBox')
    this.scrollElement && this.scrollElement.addEventListener('scroll', this.scrollHandler)
  }

  public async created() {
    this.loadData()
  }

  private async loadData() {
    if (typeof (this.dataRetriever) === 'object') {
      this.loading = true
      this.tableData = await this.dataRetriever
      this.loading = false
      this.fetchOffset = -1 // disable fetch
    } else if (typeof (this.dataRetriever) === 'function') {
      await this.getMoreData()
    }
  }

  public destroyed() {
    this.scrollElement && this.scrollElement.removeEventListener('scroll', this.scrollHandler)
  }

  private async getMoreData(getAll = false) {
    if (this.fetchOffset != -1 && this.dataRetriever && this.fetchLock.indexOf(this.fetchOffset) < 0) {
      this.loading = true
      this.cursor = "wait"
      const lastLock = this.fetchLock.push(this.fetchOffset)
      const data = getAll ? await this.dataRetriever(this.fetchOffset, maxRecords) : await this.dataRetriever(this.fetchOffset, this.fetchLimit)
      if (!data) {
        this.fetchLock.splice(lastLock - 1, 1) // remove lock if unsuccessful
        console.log('DataGrid: failed to retrieve chunk')
      } else if (data.length) {
        this.tableData.push(...data)
        this.fetchOffset += this.fetchLimit
        if (getAll || this.tableData.length < this.fetchOffset)
          this.fetchOffset = -1
      } else {
        this.fetchOffset = -1
      }
      this.loading = false
      this.cursor = "auto"
    }
  }

  public scrollHandler(e: Event) {
    const mb = e.target as HTMLElement
    if (mb) {
      const top = (window.pageYOffset || mb.scrollTop) - (mb.clientTop || 0)
      const totalHeight = mb.scrollHeight - mb.clientHeight
      if ((totalHeight - top) < mb.clientHeight) {
        this.getMoreData()
      }
    }
  }

  public mouseover() {

  }

  public mouseout() {

  }

  public async sort(field: Field) {
    this.cursor = "wait"
    setTimeout(async () => { // ugly timeout needed for browser to switch cursor icon
      // Make sure we have full dataset before sorting
      if (this.fetchOffset !== -1) await this.getMoreData(true)

      if (!field.sort) {
        return
      }
      if (field.sorted) {
        field.sortDirection =
          field.sortDirection === 1 ? -1 : 1
      } else {
        // reset sort flag for all fields
        this.tableFields.forEach((item: Field) => {
          item.sorted = false
        })
        field.sorted = true
        field.sortDirection = 1
      }

      this.tableData.sort(this.sortBy(field, this.sortingPrimer(field)))
      this.cursor = "auto"
    }, 50)
  }

  public sortBy(field: Field, primer: any): (a: any, b: any) => number {
    const key = primer ?
      (x: any) => {
        try {
          return primer(x[field.name])
        } catch {
          return x[field.name]
        }
      } :
      (x: any) => {
        return x[field.name]
      }
    return (a: any, b: any): number => {
      a = key(a)
      b = key(b)
      const x = a > b ? 1 : 0
      const y = b > a ? 1 : 0
      // sortDirection 1 Ascending, -1 Descending
      return (field.sortDirection ? field.sortDirection : 1) * (x - y)
      // return reverse * ((a > b) - (b > a))
    }
  }

  public sortingPrimer(field: Field): (x: string) => string | number | Date {
    let primer: (x: string) => string | number | Date
    const that = this
    switch (field.dataType) {
      case 'Date':
        primer = (x: any) => {
          if (x === undefined || x === null) return ""
          return that.parseDate(x)
        }
        break
      case 'Number':
        primer = (x: any) => {
          if (x === undefined || x === null) return Number.MAX_VALUE * -1
          const num = String(x).replace(/(?![\d|\.])./g, "") // remove everything except digits and decimal points
          return parseFloat(num) !== NaN ? parseFloat(num) : 0
        }
        break
      case 'Email':
        primer = (x: any) => {
          if (x === undefined || x === null) return ""
          const parts = x.split('@')
          if (parts.length === 2) {
            return parts[1] + '@' + parts[0]
          } else {
            return String(x).toLocaleUpperCase()
          }
        }
        break
      default:
        // Default is Alphabetical
        primer = (x: any) => {
          if (x === undefined || x === null) return ""
          return String(x).toLocaleUpperCase()
        }
    }
    return primer
  }

  public parseDate(dateString: string) {
    const dateTimeParts = dateString.trim().split(/[\s|T]/)
    const dateParts = dateTimeParts[0].split('/')
    if (dateTimeParts[1]) {
      const timeParts = dateTimeParts[1].split(':')
      const d = new Date(
        parseInt(dateParts[2], 10),
        parseInt(dateParts[1], 10) - 1,
        parseInt(dateParts[0], 10),
        parseInt(timeParts[0], 10),
        parseInt(timeParts[1], 10),
        parseInt(timeParts[2], 10),
      )
      return isNaN(d.getTime()) ? dateString : d
    } else {
      const d = new Date(parseInt(dateParts[2], 10), parseInt(dateParts[1], 10) - 1, parseInt(dateParts[0], 10))
      return isNaN(d.getTime()) ? dateString : d
    }
  }

  @Watch('data')
  public onDataChanged(value: object[]) {
    this.tableData = JSON.parse(JSON.stringify(value))
  }

  @Watch('fields', {immediate: true, deep: true})
  public onFieldsChanged(value: object[]) {
    if (!value) return
    this.tableFields = value
    this.tableFields.forEach((item: Field) => {
      item.sorted = false
      item.sortDirection = 1
    })
  }

  @Watch('refresh', {immediate: false})
  private refreshData() {
    if(this.loading) return
    this.fetchOffset = 0
    this.fetchLock = []
    this.tableData = []
    this.loadData()
  }
}
